import React from 'react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Card, CardContent } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Building2, Bot, Rocket, Lightbulb, Globe, GraduationCap, Smartphone, Heart, Truck } from 'lucide-react';

const MoroccoInAction = () => {
  const { t } = useTranslation();

  const initiatives = [
    {
      id: 1,
      title: t('vision30.action.smartCities.title'),
      description: t('vision30.action.smartCities.description'),
      location: t('vision30.action.smartCities.location'),
      status: t('vision30.action.smartCities.status'),
      icon: Building2,
      color: "from-primary to-accent"
    },
    {
      id: 2,
      title: t('vision30.action.aiPrograms.title'),
      description: t('vision30.action.aiPrograms.description'),
      location: t('vision30.action.aiPrograms.location'),
      status: t('vision30.action.aiPrograms.status'),
      icon: Bot,
      color: "from-accent to-primary"
    },
    {
      id: 3,
      title: t('vision30.action.innovationHubs.title'),
      description: t('vision30.action.innovationHubs.description'),
      location: t('vision30.action.innovationHubs.location'),
      status: t('vision30.action.innovationHubs.status'),
      icon: Rocket,
      color: "from-primary/80 to-accent/80"
    },
    {
      id: 4,
      title: t('vision30.action.startupEcosystem.title'),
      description: t('vision30.action.startupEcosystem.description'),
      location: t('vision30.action.startupEcosystem.location'),
      status: t('vision30.action.startupEcosystem.status'),
      icon: Lightbulb,
      color: "from-accent/80 to-primary/80"
    },
    {
      id: 5,
      title: t('vision30.action.digitalInfrastructure.title'),
      description: t('vision30.action.digitalInfrastructure.description'),
      location: t('vision30.action.digitalInfrastructure.location'),
      status: t('vision30.action.digitalInfrastructure.status'),
      icon: Globe,
      color: "from-primary to-accent"
    },
    {
      id: 6,
      title: t('vision30.action.educationTech.title'),
      description: t('vision30.action.educationTech.description'),
      location: t('vision30.action.educationTech.location'),
      status: t('vision30.action.educationTech.status'),
      icon: GraduationCap,
      color: "from-accent to-primary"
    }
  ];

  const partnerships = [
    {
      name: "Microsoft",
      // logo: "https://api.dicebear.com/7.x/initials/svg?seed=Microsoft", // Removed external API dependency
      description: t('vision30.partnerships.microsoft')
    },
    {
      name: "Google",
      // logo: "https://api.dicebear.com/7.x/initials/svg?seed=Google", // Removed external API dependency
      description: t('vision30.partnerships.google')
    },
    {
      name: "IBM",
      // logo: "https://api.dicebear.com/7.x/initials/svg?seed=IBM", // Removed external API dependency
      description: t('vision30.partnerships.ibm')
    },
    {
      name: "Amazon Web Services",
      // logo: "https://api.dicebear.com/7.x/initials/svg?seed=AWS", // Removed external API dependency
      description: t('vision30.partnerships.aws')
    }
  ];

  return (
    <section className="py-24 bg-muted/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
            {t('vision30.action.title')}
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            {t('vision30.action.subtitle')}
          </p>
        </div>

        {/* Initiatives Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {initiatives.map((initiative, index) => (
            <Card 
              key={initiative.id} 
              className="group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30 bg-background/80 backdrop-blur-sm overflow-hidden"
            >
              <CardContent className="p-0">
                {/* Header with Gradient */}
                <div className={`bg-gradient-to-r ${initiative.color} p-6 text-white`}>
                  <div className="flex items-center justify-between">
                    <div className="text-4xl">
                      {React.createElement(initiative.icon, { className: "w-10 h-10 text-white" })}
                    </div>
                    <div className="text-right">
                      <div className="text-sm opacity-90">{initiative.location}</div>
                      <div className="text-xs opacity-75">{initiative.status}</div>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                    {initiative.title}
                  </h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {initiative.description}
                  </p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Global Partnerships */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-foreground text-center mb-12">
            {t('vision30.partnerships.title')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {partnerships.map((partner, index) => (
              <Card 
                key={partner.name} 
                className="group hover:shadow-lg transition-all duration-300 border-border/50 hover:border-primary/30 bg-background/50 backdrop-blur-sm"
              >
                <CardContent className="p-6 text-center">
                  <Avatar className="w-16 h-16 mx-auto mb-4">
                    <AvatarImage src={partner.logo} alt={partner.name} />
                    <AvatarFallback>{partner.name.charAt(0)}</AvatarFallback>
                  </Avatar>
                  <h4 className="font-semibold text-foreground mb-2">{partner.name}</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {partner.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Success Stories */}
        <div className="bg-gradient-to-r from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 md:p-12">
          <h3 className="text-3xl font-bold text-foreground text-center mb-8">
            {t('vision30.successStories.title')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('vision30.successStories.story1.title')}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('vision30.successStories.story1.description')}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('vision30.successStories.story2.title')}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('vision30.successStories.story2.description')}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <Truck className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('vision30.successStories.story3.title')}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('vision30.successStories.story3.description')}
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                  <GraduationCap className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-2">
                    {t('vision30.successStories.story4.title')}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {t('vision30.successStories.story4.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MoroccoInAction;
